import React, { useEffect, useState } from 'react';
import { SaveIcon, InfoIcon, ClockIcon, UploadCloudIcon, GlobeIcon } from 'lucide-react';
export function CountryDetail({
  country,
  onSave,
  onCancel,
  isCreating,
  locale
}) {
  const [activeTab, setActiveTab] = useState('details');
  const [formData, setFormData] = useState({
    alpha2Code: '',
    alpha3Code: '',
    numericCode: '',
    commonName: '',
    officialName: '',
    officialCurrency: '',
    phoneCode: '',
    region: '',
    subRegion: '',
    customFields: {}
  });
  // Sample history data - in real app, this would come from API
  const [history, setHistory] = useState([{
    id: 1,
    eventType: 'updated',
    eventDescription: 'Official name updated',
    timestamp: '2026-01-19T14:30:00Z',
    userId: 'user-123',
    userName: 'John Smith',
    changes: [{
      field: 'officialName',
      oldValue: 'Canada',
      newValue: 'Canada'
    }]
  }, {
    id: 2,
    eventType: 'updated',
    eventDescription: 'Phone code updated',
    timestamp: '2026-01-15T09:15:00Z',
    userId: 'user-456',
    userName: 'Jane Doe',
    changes: [{
      field: 'phoneCode',
      oldValue: '+1',
      newValue: '+1'
    }]
  }, {
    id: 3,
    eventType: 'created',
    eventDescription: 'Country created',
    timestamp: '2026-01-10T16:45:00Z',
    userId: 'user-789',
    userName: 'Admin User',
    changes: []
  }]);
  // Available regions and subregions for dropdown selection
  const regions = ['Africa', 'Americas', 'Asia', 'Europe', 'Oceania'];
  const subRegionsByRegion = {
    Africa: ['Northern Africa', 'Eastern Africa', 'Middle Africa', 'Southern Africa', 'Western Africa'],
    Americas: ['Northern America', 'Caribbean', 'Central America', 'South America'],
    Asia: ['Central Asia', 'Eastern Asia', 'Southern Asia', 'South-Eastern Asia', 'Western Asia'],
    Europe: ['Eastern Europe', 'Northern Europe', 'Southern Europe', 'Western Europe'],
    Oceania: ['Australia and New Zealand', 'Melanesia', 'Micronesia', 'Polynesia']
  };
  // Common currencies for quick selection
  const commonCurrencies = [{
    code: 'USD',
    name: 'US Dollar'
  }, {
    code: 'EUR',
    name: 'Euro'
  }, {
    code: 'GBP',
    name: 'British Pound'
  }, {
    code: 'JPY',
    name: 'Japanese Yen'
  }, {
    code: 'AUD',
    name: 'Australian Dollar'
  }, {
    code: 'CAD',
    name: 'Canadian Dollar'
  }, {
    code: 'CHF',
    name: 'Swiss Franc'
  }, {
    code: 'CNY',
    name: 'Chinese Yuan'
  }, {
    code: 'INR',
    name: 'Indian Rupee'
  }, {
    code: 'SGD',
    name: 'Singapore Dollar'
  }];
  useEffect(() => {
    if (country && !isCreating) {
      setFormData({
        ...country,
        // Handle legacy data that might only have 'name' field
        commonName: country.commonName || country.name || '',
        officialName: country.officialName || '',
        customFields: {
          ...country.customFields
        }
      });
    } else {
      // Default values for new country
      setFormData({
        alpha2Code: '',
        alpha3Code: '',
        numericCode: '',
        commonName: '',
        officialName: '',
        officialCurrency: '',
        phoneCode: '',
        region: '',
        subRegion: '',
        customFields: {}
      });
    }
  }, [country, isCreating]);
  const handleChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  const handleRegionChange = e => {
    const newRegion = e.target.value;
    setFormData({
      ...formData,
      region: newRegion,
      // Reset subregion when region changes
      subRegion: ''
    });
  };
  const handleSubmit = e => {
    e.preventDefault();
    onSave(formData);
  };
  const formatTimestamp = timestamp => {
    const date = new Date(timestamp);
    return date.toLocaleString(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };
  const getEventIcon = eventType => {
    switch (eventType) {
      case 'created':
        return <UploadCloudIcon className="h-5 w-5" data-id="element-15524" />;
      case 'updated':
        return <InfoIcon className="h-5 w-5" data-id="element-15525" />;
      default:
        return <InfoIcon className="h-5 w-5" data-id="element-15526" />;
    }
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-15527">
      <div className="px-4 py-5 sm:px-6 flex justify-between items-center border-b border-gray-200" data-id="element-15528">
        <div data-id="element-15529">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-15530">
            {isCreating ? 'Add New Country' : `${formData.commonName || formData.name} (${formData.alpha2Code})`}
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-15531">
            {isCreating ? 'Enter details for the new country' : 'View and update country details'}
          </p>
        </div>
        {activeTab === 'details' && <div className="flex space-x-3" data-id="element-15532">
            <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15533">
              Cancel
            </button>
            <button type="button" onClick={handleSubmit} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15534">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-15535" />
              Save Country
            </button>
          </div>}
      </div>

      {/* Tabs */}
      {!isCreating && <div className="border-b border-gray-200" data-id="element-15536">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs" data-id="element-15537">
            <button onClick={() => setActiveTab('details')} className={`${activeTab === 'details' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-15538">
              Details
            </button>
            <button onClick={() => setActiveTab('relationships')} className={`${activeTab === 'relationships' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-15539">
              Relationships
            </button>
            <button onClick={() => setActiveTab('history')} className={`${activeTab === 'history' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-15540">
              History
            </button>
          </nav>
        </div>}

      {/* Tab Content */}
      <div className="px-4 py-5 sm:px-6" data-id="element-15541">
        {/* Details Tab */}
        {activeTab === 'details' && <form className="space-y-6" onSubmit={handleSubmit} data-id="element-15542">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-15543">
              {/* Basic Information */}
              <div className="sm:col-span-6" data-id="element-15544">
                <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-15545">
                  Basic Information
                </h4>
              </div>

              <div className="sm:col-span-3" data-id="element-15546">
                <label htmlFor="commonName" className="block text-sm font-medium text-gray-700" data-id="element-15547">
                  Common Name <span className="text-red-500" data-id="element-15548">*</span>
                </label>
                <div className="mt-1" data-id="element-15549">
                  <input type="text" name="commonName" id="commonName" value={formData.commonName} onChange={handleChange} placeholder="e.g., Sri Lanka" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-15550" />
                </div>
                <p className="mt-1 text-xs text-gray-500" data-id="element-15551">
                  The commonly used name for the country
                </p>
              </div>

              <div className="sm:col-span-3" data-id="element-15552">
                <label htmlFor="officialName" className="block text-sm font-medium text-gray-700" data-id="element-15553">
                  Official Name
                </label>
                <div className="mt-1" data-id="element-15554">
                  <input type="text" name="officialName" id="officialName" value={formData.officialName} onChange={handleChange} placeholder="e.g., Democratic Socialist Republic of Sri Lanka" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-15555" />
                </div>
                <p className="mt-1 text-xs text-gray-500" data-id="element-15556">
                  The official constitutional name (optional)
                </p>
              </div>

              {/* Info banner about name usage */}
              <div className="sm:col-span-6" data-id="element-15557">
                <div className="bg-blue-50 border-l-4 border-blue-400 p-3" data-id="element-15558">
                  <div className="flex" data-id="element-15559">
                    <div className="flex-shrink-0" data-id="element-15560">
                      <InfoIcon className="h-5 w-5 text-blue-400" data-id="element-15561" />
                    </div>
                    <div className="ml-3" data-id="element-15562">
                      <p className="text-sm text-blue-700" data-id="element-15563">
                        The <strong data-id="element-15564">common name</strong> will be used throughout
                        the application for display purposes. The{' '}
                        <strong data-id="element-15565">official name</strong> can be used for formal
                        documents and reports.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="sm:col-span-3" data-id="element-15566">
                <label htmlFor="phoneCode" className="block text-sm font-medium text-gray-700" data-id="element-15567">
                  Phone Code
                </label>
                <div className="mt-1" data-id="element-15568">
                  <input type="text" name="phoneCode" id="phoneCode" value={formData.phoneCode} onChange={handleChange} placeholder="+XX" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-15569" />
                </div>
              </div>

              {/* ISO Codes */}
              <div className="sm:col-span-6" data-id="element-15570">
                <h4 className="text-sm font-medium text-gray-900 mb-3 mt-4" data-id="element-15571">
                  ISO Codes
                </h4>
              </div>

              <div className="sm:col-span-2" data-id="element-15572">
                <label htmlFor="alpha2Code" className="block text-sm font-medium text-gray-700" data-id="element-15573">
                  Alpha-2 Code (ISO 3166-1){' '}
                  <span className="text-red-500" data-id="element-15574">*</span>
                </label>
                <div className="mt-1" data-id="element-15575">
                  <input type="text" name="alpha2Code" id="alpha2Code" value={formData.alpha2Code} onChange={handleChange} maxLength={2} placeholder="XX" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md uppercase" required disabled={!isCreating} data-id="element-15576" />
                </div>
                {!isCreating && <p className="mt-1 text-xs text-gray-500" data-id="element-15577">
                    Alpha-2 code cannot be changed after creation
                  </p>}
              </div>

              <div className="sm:col-span-2" data-id="element-15578">
                <label htmlFor="alpha3Code" className="block text-sm font-medium text-gray-700" data-id="element-15579">
                  Alpha-3 Code (ISO 3166-1){' '}
                  <span className="text-red-500" data-id="element-15580">*</span>
                </label>
                <div className="mt-1" data-id="element-15581">
                  <input type="text" name="alpha3Code" id="alpha3Code" value={formData.alpha3Code} onChange={handleChange} maxLength={3} placeholder="XXX" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md uppercase" required data-id="element-15582" />
                </div>
              </div>

              <div className="sm:col-span-2" data-id="element-15583">
                <label htmlFor="numericCode" className="block text-sm font-medium text-gray-700" data-id="element-15584">
                  Numeric Code (ISO 3166-1){' '}
                  <span className="text-red-500" data-id="element-15585">*</span>
                </label>
                <div className="mt-1" data-id="element-15586">
                  <input type="text" name="numericCode" id="numericCode" value={formData.numericCode} onChange={handleChange} maxLength={3} placeholder="XXX" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-15587" />
                </div>
              </div>

              {/* Geography */}
              <div className="sm:col-span-6" data-id="element-15588">
                <h4 className="text-sm font-medium text-gray-900 mb-3 mt-4" data-id="element-15589">
                  Geography
                </h4>
              </div>

              <div className="sm:col-span-3" data-id="element-15590">
                <label htmlFor="region" className="block text-sm font-medium text-gray-700" data-id="element-15591">
                  Region <span className="text-red-500" data-id="element-15592">*</span>
                </label>
                <div className="mt-1" data-id="element-15593">
                  <select id="region" name="region" value={formData.region} onChange={handleRegionChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-15594">
                    <option value="" data-id="element-15595">Select a region</option>
                    {regions.map(region => <option key={region} value={region} data-id="element-15596">
                        {region}
                      </option>)}
                  </select>
                </div>
              </div>

              <div className="sm:col-span-3" data-id="element-15597">
                <label htmlFor="subRegion" className="block text-sm font-medium text-gray-700" data-id="element-15598">
                  Sub-Region <span className="text-red-500" data-id="element-15599">*</span>
                </label>
                <div className="mt-1" data-id="element-15600">
                  <select id="subRegion" name="subRegion" value={formData.subRegion} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" disabled={!formData.region} required data-id="element-15601">
                    <option value="" data-id="element-15602">Select a sub-region</option>
                    {formData.region && subRegionsByRegion[formData.region]?.map(subRegion => <option key={subRegion} value={subRegion} data-id="element-15603">
                          {subRegion}
                        </option>)}
                  </select>
                </div>
              </div>

              {/* Currency */}
              <div className="sm:col-span-6" data-id="element-15604">
                <h4 className="text-sm font-medium text-gray-900 mb-3 mt-4" data-id="element-15605">
                  Currency
                </h4>
              </div>

              <div className="sm:col-span-3" data-id="element-15606">
                <label htmlFor="officialCurrency" className="block text-sm font-medium text-gray-700" data-id="element-15607">
                  Official Currency <span className="text-red-500" data-id="element-15608">*</span>
                </label>
                <div className="mt-1" data-id="element-15609">
                  <select id="officialCurrency" name="officialCurrency" value={formData.officialCurrency} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-15610">
                    <option value="" data-id="element-15611">Select a currency</option>
                    {commonCurrencies.map(currency => <option key={currency.code} value={currency.code} data-id="element-15612">
                        {currency.code} - {currency.name}
                      </option>)}
                    <option value="OTHER" data-id="element-15613">Other (enter manually)</option>
                  </select>
                </div>
              </div>

              {formData.officialCurrency === 'OTHER' && <div className="sm:col-span-3" data-id="element-15614">
                  <label htmlFor="customCurrency" className="block text-sm font-medium text-gray-700" data-id="element-15615">
                    Custom Currency Code
                  </label>
                  <div className="mt-1" data-id="element-15616">
                    <input type="text" name="officialCurrency" id="customCurrency" value={formData.officialCurrency === 'OTHER' ? '' : formData.officialCurrency} onChange={handleChange} maxLength={3} placeholder="XXX" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md uppercase" required data-id="element-15617" />
                  </div>
                </div>}
            </div>

            <div className="flex justify-end space-x-3 pt-5 border-t border-gray-200" data-id="element-15618">
              <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15619">
                Cancel
              </button>
              <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15620">
                <SaveIcon className="h-4 w-4 mr-2" data-id="element-15621" />
                {isCreating ? 'Create Country' : 'Update Country'}
              </button>
            </div>
          </form>}

        {/* Relationships Tab */}
        {activeTab === 'relationships' && <div className="py-4" data-id="element-15622">
            <div className="text-center py-12" data-id="element-15623">
              <GlobeIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-15624" />
              <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-15625">
                No relationships
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-15626">
                Country relationships will be displayed here.
              </p>
            </div>
          </div>}

        {/* History Tab */}
        {activeTab === 'history' && <div className="py-4" data-id="element-15627">
            <div className="bg-white rounded-lg border border-gray-200" data-id="element-15628">
              <div className="px-6 py-4 border-b border-gray-200" data-id="element-15629">
                <h3 className="text-base font-semibold text-gray-900" data-id="element-15630">
                  Country History
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-15631">
                  {history.length} events
                </p>
              </div>

              <div className="px-6 py-6" data-id="element-15632">
                {history.length === 0 ? <div className="text-center py-12" data-id="element-15633">
                    <ClockIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-15634" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-15635">
                      No history
                    </h3>
                    <p className="mt-1 text-sm text-gray-500" data-id="element-15636">
                      No changes have been recorded for this country yet.
                    </p>
                  </div> : <div className="flow-root" data-id="element-15637">
                    <ul className="-mb-8" data-id="element-15638">
                      {history.map((event, eventIdx) => <li key={event.id} data-id="element-15639">
                          <div className="relative pb-8" data-id="element-15640">
                            {eventIdx !== history.length - 1 && <span className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" data-id="element-15641" />}
                            <div className="relative flex items-start space-x-3" data-id="element-15642">
                              <div data-id="element-15643">
                                <div className="relative px-1" data-id="element-15644">
                                  <div className="h-10 w-10 bg-gray-100 rounded-full ring-8 ring-white flex items-center justify-center" data-id="element-15645">
                                    {getEventIcon(event.eventType)}
                                  </div>
                                </div>
                              </div>
                              <div className="min-w-0 flex-1" data-id="element-15646">
                                <div data-id="element-15647">
                                  <div className="text-sm font-medium text-gray-900" data-id="element-15648">
                                    {event.eventDescription}
                                  </div>
                                  <p className="mt-0.5 text-sm text-gray-500" data-id="element-15649">
                                    {formatTimestamp(event.timestamp)}
                                  </p>
                                </div>
                                {event.changes && event.changes.length > 0 && <div className="mt-2 text-sm text-gray-700" data-id="element-15650">
                                    <div className="bg-gray-50 rounded-md p-3 space-y-1" data-id="element-15651">
                                      {event.changes.map((change, idx) => <div key={idx} className="flex items-center text-xs" data-id="element-15652">
                                          <span className="font-medium text-gray-700" data-id="element-15653">
                                            {change.field}:
                                          </span>
                                          <span className="ml-2 text-gray-500 line-through" data-id="element-15654">
                                            {change.oldValue}
                                          </span>
                                          <span className="mx-2 text-gray-400" data-id="element-15655">
                                            →
                                          </span>
                                          <span className="text-gray-900" data-id="element-15656">
                                            {change.newValue}
                                          </span>
                                        </div>)}
                                    </div>
                                  </div>}
                              </div>
                            </div>
                          </div>
                        </li>)}
                    </ul>
                  </div>}
              </div>
            </div>
          </div>}
      </div>
    </div>;
}